"use client";

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import cn from "@/utils/cn";

import { AccordionItemProps } from "./types";

const AccordionItem: FC<AccordionItemProps> = ({
  accordionId,
  title,
  expanded,
  openIcon,
  closeIcon,
  children,
  onSelect,
  callbackOnOpen,
  tag = "div",
  innerTag,
  useButton = true,
  classes,
}) => {
  const accordionPanel = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);

  const [itemExpanded, setItemExpanded] = useState<boolean>(() => (expanded !== undefined ? expanded : false));

  useEffect(() => {
    setItemExpanded(expanded !== undefined ? expanded : false);
  }, [expanded]);

  const accordionIcon = useMemo(() => {
    let iconClass = "accordion-icon";
    if (openIcon || closeIcon) {
      if (openIcon && closeIcon) {
        iconClass = `icon--${itemExpanded ? closeIcon : openIcon}`;
      } else if (!closeIcon) {
        iconClass = `icon--${openIcon}`;
      } else if (!openIcon) {
        iconClass = `icon--${closeIcon}`;
      }
    }
    return <i className={iconClass}></i>;
  }, [closeIcon, itemExpanded, openIcon]);

  const toggle = useCallback(() => {
    if (!itemExpanded) {
      callbackOnOpen && callbackOnOpen();
    }
    setItemExpanded(!itemExpanded);
  }, [callbackOnOpen, itemExpanded]);

  function renderAccordionTitle() {
    return (
      <>
        {innerTag === "h3" ? (
          <h3 className="accordion-title font-primary">
            {title} {useButton && accordionIcon}
          </h3>
        ) : (
          <span className="accordion-title font-primary">
            {title} {useButton && accordionIcon}
          </span>
        )}
      </>
    );
  }

  return (
    <div ref={container} className={"accordion__item"}>
      {useButton ? (
        <>
          <button
            ref={opener}
            type="button"
            id={accordionId}
            onClick={() => {
              onSelect && onSelect({ expanded: !itemExpanded });
              toggle();
            }}
            className={cn("accordion-trigger", classes && classes)}
            aria-expanded={itemExpanded}
            aria-controls={accordionId + "-panel"}
          >
            {renderAccordionTitle()}
          </button>
          <div
            ref={accordionPanel}
            className={cn("accordion-panel", "ease-linear")}
            role="region"
            id={accordionId + "-panel"}
            aria-labelledby={accordionId}
          >
            {itemExpanded && children}
          </div>
        </>
      ) : (
        <>
          <div className={cn("accordion-trigger", classes && classes)}>{renderAccordionTitle()}</div>
          <div className={cn("accordion-panel", "ease-linear")}>{itemExpanded && children}</div>
        </>
      )}
    </div>
  );
};

export default AccordionItem;
